import React, { useMemo, useState } from 'react';

import { theme } from '~/theme';

import { ExpandMore } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  styled,
} from '@mui/material';

import { AvailabilityDebugResponse, PricingIssue, Rate } from '~/services/AccommodationService';

import PricingIssueContent from './PricingIssueContent';

const SidebarDrawer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: '100%',
  height: '100%',
  width: 300,
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  borderLeft: `1px solid ${theme.palette.divider}`,
  transition: 'width 0.3s ease',
  overflow: 'visible',
  boxShadow: theme.shadows[1],
}));

const SidebarContent = styled(Box)({
  width: 300,
  height: '100%',
  overflow: 'auto',
});

interface RoomWithPricingIssues {
  id: string;
  name: string;
  pricingIssues: Array<PricingIssue>;
}

interface PricingIssuesModalProps {
  availabilityDebugResponse: AvailabilityDebugResponse;
  closeModal: () => void;
}

const getRateName = (rate: PricingIssue) => {
  const rateMeta = rate.rate.meta as Rate['meta'];
  const hasFlightBundle = rateMeta.packaged;
  return rate.rate.name + (hasFlightBundle ? ' (Packaged)' : '');
};

export default function PricingIssuesModal({ availabilityDebugResponse, closeModal }: PricingIssuesModalProps) {
  const roomsWithPricingIssues = availabilityDebugResponse.debug.pricingIssues.reduce<
    Record<string, RoomWithPricingIssues>
  >((acc, rate) => {
    if (!acc[rate.roomId]) {
      const room = Object.values(availabilityDebugResponse.response.properties)[0].rooms[rate.roomId];
      acc[rate.roomId] = { id: rate.roomId, name: room.name, pricingIssues: [] };
    }
    acc[rate.roomId].pricingIssues.push(rate);
    return acc;
  }, {});

  const [selectedRate, setSelectedRate] = useState<PricingIssue>(
    Object.values(roomsWithPricingIssues)[0].pricingIssues[0],
  );

  const selectedRoom = useMemo(() => {
    return roomsWithPricingIssues[selectedRate.roomId];
  }, [selectedRate, roomsWithPricingIssues]);

  return (
    <Dialog
      open
      onClose={closeModal}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          position: 'relative',
          overflow: 'visible',
          minHeight: '50vh',
        },
      }}
    >
      <SidebarDrawer>
        <SidebarContent>
          <Box width={300} p={2}>
            <Typography variant="h6">Rooms</Typography>
            {Object.values(roomsWithPricingIssues).map((room) => (
              <Accordion key={room.id} defaultExpanded={selectedRate.roomId === room.id}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography fontWeight="bold" fontSize="small">
                    {room.name}
                  </Typography>
                </AccordionSummary>
                <List>
                  {room.pricingIssues.map((rate) => (
                    <ListItem disablePadding key={rate.id}>
                      <ListItemButton onClick={() => setSelectedRate(rate)} selected={selectedRate.id === rate.id}>
                        <ListItemText primary={<Typography fontSize="small">{getRateName(rate)}</Typography>} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Accordion>
            ))}
          </Box>
        </SidebarContent>
      </SidebarDrawer>
      <DialogTitle>
        <Stack>
          {getRateName(selectedRate)}
          <Typography variant="caption" color="text.secondary">
            {selectedRoom?.name}
          </Typography>
        </Stack>
        <IconButton
          aria-label="close"
          onClick={closeModal}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <PricingIssueContent pricingIssue={selectedRate} />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeModal}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
